<template>
<div>

    <not-allowed v-if="!AllowedDisplay" :data="datas"></not-allowed>

    <div v-if="show" class="facturx-view">
        <div class="d-flex justify-end mb-3">
            <v-btn small color="primary" @click="downloadFile">Télécharger le XML Factur-X</v-btn>
        </div>

        <v-row dense>
            <v-col cols="12" md="4">
                <v-card outlined class="fill-height compact-card">
                    <v-card-title>Facture</v-card-title>
                    <v-card-text>
                        <div class="info-grid">
                            <span>Numéro</span><strong>{{ value('number') }}</strong>
                            <span>Date</span><strong>{{ value('issuedate') }}</strong>
                            <span>Type</span><strong>{{ value('type') }} ({{ value('typecode') }})</strong>
                            <span>Processus</span><v-chip x-small color="primary" text-color="white">{{ value('business_process') }}</v-chip>
                            <span>Devise</span><strong>{{ value('currency') }}</strong>
                            <span>Réf. acheteur</span><strong>{{ value('buyer_ref') }}</strong>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" md="4" v-for="party in parties" :key="party.prefix">
                <v-card outlined class="fill-height compact-card">
                    <v-card-title>{{ party.title }}</v-card-title>
                    <v-card-text>
                        <div class="party-name">{{ partyValue(party.prefix, 'name') }}</div>
                        <div>{{ partyAddress(party.prefix) }}</div>
                        <div>
                            {{ partyValue(party.prefix, 'postal') }}
                            {{ partyValue(party.prefix, 'city') }} ·
                            {{ partyValue(party.prefix, 'country') }}
                        </div>
                        <v-divider class="my-2" />
                        <div><strong>Identifiant :</strong> {{ partyValue(party.prefix, 'companieid') }} <span class="grey--text">({{ partyValue(party.prefix, 'companieidscheme') }})</span></div>
                        <div><strong>TVA :</strong> {{ partyValue(party.prefix, 'vatnumber') }}</div>
                        <div><strong>Adresse PDP :</strong> {{ partyValue(party.prefix, 'electronicadress') }}</div>
                        <div><strong>E-mail :</strong> {{ partyValue(party.prefix, 'email') }}</div>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" v-if="datas.values.notes && datas.values.notes.length">
                <v-card outlined class="compact-card">
                    <v-card-title>Mentions</v-card-title>
                    <v-card-text class="notes-grid">
                        <div v-for="note in datas.values.notes" :key="note.code" class="note-item">
                            <v-chip x-small outlined color="primary">{{ note.code }}</v-chip>
                            <span>{{ note.content }}</span>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" v-if="invoiceEvents.length">
                <v-card outlined class="compact-card">
                    <v-card-title class="event-title">
                        <span>Suivi plateforme</span>
                        <v-chip
                            small
                            :color="statusColor(latestEvent.status)"
                            text-color="white"
                        >
                            Dernier statut : {{ statusLabel(latestEvent.status) }}
                        </v-chip>
                    </v-card-title>
                    <v-simple-table dense>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Statut</th>
                                <th>Code plateforme</th>
                                <th>Détails</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(event,index) in invoiceEvents" :key="event.id || index">
                                <td class="event-date">{{ eventDate(event.date) }}</td>
                                <td>
                                    <v-chip x-small :color="statusColor(event.status)" text-color="white">
                                        {{ statusLabel(event.status) }}
                                    </v-chip>
                                    <div v-if="event.label" class="caption grey--text mt-1">{{ event.label }}</div>
                                </td>
                                <td>{{ event.provider_status || '-' }}</td>
                                <td class="event-details">{{ eventDetails(event.details) }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card>
            </v-col>

            <v-col cols="12">
                <v-card outlined class="compact-card">
                    <v-card-title>Lignes de facture</v-card-title>
                    <v-simple-table dense>
                        <thead>
                            <tr>
                                <th>Désignation</th>
                                <th class="text-right">Quantité</th>
                                <th>Unité</th>
                                <th class="text-right">Prix unitaire</th>
                                <th class="text-right">TVA</th>
                                <th class="text-right">Total HT</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(product,index) in datas.values.products" :key="index">
                                <td>
                                    <strong>{{ product.name }}</strong>
                                    <div class="caption grey--text" v-if="product.description">{{ product.description }}</div>
                                </td>
                                <td class="text-right">{{ product.quantity }}</td>
                                <td>{{ product.unit }} ({{ product.unitcode }})</td>
                                <td class="text-right">{{ money(product.price) }}</td>
                                <td class="text-right">{{ product.vat }} %</td>
                                <td class="text-right"><strong>{{ money(product.price_net) }}</strong></td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card>
            </v-col>

            <v-col cols="12" md="7">
                <v-card outlined class="fill-height compact-card">
                    <v-card-title>Paiement et TVA</v-card-title>
                    <v-card-text>
                        <div v-for="(payment,index) in datas.values.payments" :key="'payment-'+index" class="mb-2">
                            <strong>{{ payment.description || 'Paiement' }}</strong>
                            <span class="ml-2">Code {{ payment.code }}</span>
                            <span class="ml-2" v-if="payment.iban">IBAN {{ payment.iban }}</span>
                        </div>
                        <div v-for="(term,index) in datas.values.payment_terms" :key="'term-'+index" class="caption mb-1">
                            {{ term }}
                        </div>
                        <v-chip v-for="(vat,index) in datas.values.vat" :key="'vat-'+index" small outlined class="mr-2 mt-2">
                            TVA {{ vat.base }} % : {{ money(vat.montant) }} sur {{ money(vat.basemontant) }}
                        </v-chip>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" md="5">
                <v-card outlined class="compact-card totals-card">
                    <v-card-title>Totaux</v-card-title>
                    <v-card-text>
                        <div><span>Total HT</span><strong>{{ money(datas.values.totalht) }}</strong></div>
                        <div><span>Déjà payé</span><strong>{{ money(datas.values.paidAmount) }}</strong></div>
                        <div v-if="Number(datas.values.roundamount) !== 0"><span>Arrondi</span><strong>{{ money(datas.values.roundamount) }}</strong></div>
                        <div><span>Total TTC</span><strong>{{ money(datas.values.totalttc) }}</strong></div>
                        <v-divider class="my-2" />
                        <div class="payable"><span>Net à payer</span><strong>{{ money(datas.values.total_payable) }}</strong></div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </div>

</div>
</template>



<script>
import { client } from '../mixins'; 

    export default {

    mixins : [client],
    name : 'facturXMod',
        data: function() {
            
            return {
                valid : true,
                show : false,
                AllowedDisplay : true,
                list : [],
                searchvalues : [],
                params : {},
                datas : [],
                dependencies : [],
                typeSave : '',
                online : false,
                keyForm : '',
                valFields : {},
                url : '',
                parties : [
                    {title: 'Vendeur', prefix: 'seller'},
                    {title: 'Acheteur', prefix: 'buyer'}
                ],
            }
        },

        computed: {
            invoiceEvents() {
                if(!Array.isArray(this.datas.events)) return []

                return [...this.datas.events].sort((a, b) => {
                    return new Date(b.date).getTime() - new Date(a.date).getTime()
                })
            },

            latestEvent() {
                return this.invoiceEvents[0] || {status: 'unknown'}
            },
        },
        watch: {

        },

        methods :  {
            value(key) {
                const result = this.datas.values[key]
                return result === null || result === undefined || result === '' ? '-' : result
            },

            partyValue(prefix, key) {
                return this.value(prefix + '_' + key)
            },

            partyAddress(prefix) {
                const lines = this.datas.values[prefix + '_address'] || []
                return lines.filter(Boolean).join(', ') || '-'
            },

            money(amount) {
                if(amount === null || amount === undefined || amount === '') return '-'
                return new Intl.NumberFormat('fr-FR', {
                    style: 'currency',
                    currency: this.datas.values.currency || 'EUR'
                }).format(amount)
            },

            eventDate(date) {
                if(!date) return '-'
                const parsed = new Date(date)
                if(Number.isNaN(parsed.getTime())) return date

                return new Intl.DateTimeFormat('fr-FR', {
                    dateStyle: 'short',
                    timeStyle: 'short'
                }).format(parsed)
            },

            statusLabel(status) {
                const labels = {
                    submitted: 'Déposée',
                    received: 'Reçue',
                    delivered: 'Mise à disposition',
                    accepted: 'Acceptée',
                    partially_accepted: 'Acceptée partiellement',
                    disputed: 'En litige',
                    suspended: 'Suspendue',
                    completed: 'Complétée',
                    rejected: 'Rejetée',
                    payment_sent: 'Paiement transmis',
                    paid: 'Encaissée',
                    invalid: 'Invalide',
                    cancelled: 'Annulée',
                    unknown: 'Inconnu'
                }

                return labels[status] || status || labels.unknown
            },

            statusColor(status) {
                if(['accepted', 'completed', 'paid'].includes(status)) return 'success'
                if(['rejected', 'invalid', 'cancelled'].includes(status)) return 'error'
                if(['partially_accepted', 'disputed', 'suspended'].includes(status)) return 'warning'
                if(['submitted', 'received', 'delivered', 'payment_sent'].includes(status)) return 'primary'
                return 'grey'
            },

            eventDetails(details) {
                if(!details || (Array.isArray(details) && !details.length)) return '-'
                if(typeof details === 'string') return details

                return Object.entries(details).map(([key, value]) => {
                    const content = typeof value === 'object' ? JSON.stringify(value) : value
                    return key + ' : ' + content
                }).join(' · ')
            },

            callAPI : function() {
                this.API({type:'post', url:'displaymodule', params:{id:this.idmodule,params:this.params}}).then((resp) => {

                  this.manageDatas(resp.data);

                })


            },   

            manageDatas(data) {
                this.datas = data
                if(!data.success) {
                this.AllowedDisplay = false; 
                return false;
                }

                this.show = true  
            },

                downloadFile() {
                    
                    this.API({type:'post',url:'displaymodule',params:{id:this.idmodule,params:this.params,export:true}, apidefault:{responseType:'blob',headers:{'Accept':'application/xml'}}}).then((resp) => {

                        const url = window.URL.createObjectURL(new Blob([resp.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'facture.xml');
                        document.body.appendChild(link);
                        link.click();
                    })
                    

                },  

        },
        mounted() {

            this.params = this.$store.getters.specificPage(this.modalKey).params    
            this.querys = this.$store.getters.specificPage(this.modalKey).querys

           
           let getdatas = this.$store.getters.getModuleDatas({page:this.modalKey,id:this.idmodule});
                if(getdatas) {
                    this.manageDatas(getdatas.datas)
                    this.$store.dispatch('removeModuleDatas',{page:this.modalKey,id:this.idmodule})
                } else {
                    this.callAPI() 
                } 
 
        },

    }

</script>

<style scoped>
.facturx-view {
    padding: 8px;
}

.compact-card .v-card__title {
    font-size: 1rem;
    padding: 10px 14px 4px;
}

.compact-card .v-card__text {
    padding: 8px 14px 12px;
    line-height: 1.45;
}

.info-grid {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 5px 12px;
    align-items: center;
}

.info-grid > span {
    color: #6b7280;
}

.party-name {
    color: #1f2937;
    font-size: 1rem;
    font-weight: 600;
}

.notes-grid {
    display: grid;
    gap: 7px;
}

.note-item {
    display: grid;
    grid-template-columns: 52px 1fr;
    gap: 8px;
    align-items: center;
}

.event-title {
    align-items: center;
    display: flex;
    justify-content: space-between;
    gap: 12px;
}

.event-date {
    white-space: nowrap;
}

.event-details {
    max-width: 430px;
    white-space: normal;
    word-break: break-word;
}

.totals-card .v-card__text > div {
    display: flex;
    justify-content: space-between;
    gap: 16px;
}

.totals-card .payable {
    color: #1f2937;
    font-size: 1.05rem;
}
</style>
